var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React, { createContext, useContext, useMemo } from 'react';
import Fuse from 'fuse.js';
import { useQuery } from '@tanstack/react-query';
import { TagType, } from '@common/api';
import { isHalfYearTag, isInternalTag, isMonthTag, isQuarterTag, isVersionTag, isYearTag, isYearToDateTag, TagQueryKeys, } from './types';
import { getCannonicalTagName, getTagDisplayName, getTagLowercaseName } from '@data/tag/tagUtil';
import { tagRelationsApi, tagsApi } from '@data/rest/api';
import LoadingAnimation from '../../base/LoadingAnimation';
var TagContext = createContext(undefined);
function getTags() {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, tagsApi.list()];
        });
    });
}
function getRelations() {
    return tagRelationsApi.list();
}
export function TagContextProvider(_a) {
    var children = _a.children;
    var _b = useQuery(TagQueryKeys.Tags, getTags, {
        staleTime: 30 * 1000,
        notifyOnChangeProps: ['data', 'isLoading', 'isLoadingError', 'isInitialLoading'],
    }), tagsData = _b.data, tagsLoading = _b.isLoading, tagsLoadingError = _b.isLoadingError, tagsDataInitialLoading = _b.isInitialLoading;
    var _c = useQuery(TagQueryKeys.Relations, getRelations, {
        staleTime: 30 * 1000,
        notifyOnChangeProps: ['data', 'isLoading', 'isLoadingError', 'isInitialLoading'],
    }), tagsRelationsData = _c.data, tagsRelationsLoading = _c.isLoading, tagsRelationsLoadingError = _c.isLoadingError, tagsRelationsInitialLoading = _c.isInitialLoading;
    var tagData = useMemo(function () {
        if (tagsData && tagsRelationsData) {
            return {
                tagData: new TagData(tagsData, tagsRelationsData),
                tagsLoading: tagsLoading || tagsRelationsLoading,
                tagsLoadingError: tagsLoadingError || tagsRelationsLoadingError,
            };
        }
        return {
            tagData: new TagData([], []),
            tagsLoading: tagsLoading || tagsRelationsLoading,
            tagsLoadingError: tagsLoadingError || tagsRelationsLoadingError,
        };
    }, [tagsData, tagsRelationsData, tagsLoading, tagsRelationsLoading]);
    var isInitialLoad = (tagsDataInitialLoading && !tagData) || (tagsRelationsInitialLoading && !tagsRelationsData);
    return (React.createElement(TagContext.Provider, { value: tagData }, isInitialLoad ? React.createElement(LoadingAnimation, { message: "Loading data..." }) : children));
}
export function useTagContext() {
    var ctx = useContext(TagContext);
    if (ctx === undefined) {
        throw new Error('useTagContext must be used within a TagContextProvider');
    }
    return ctx;
}
var TagData = /** @class */ (function () {
    function TagData(tags, relations) {
        var e_1, _a, e_2, _b, e_3, _c, e_4, _d, e_5, _e, e_6, _f;
        this.tags = tags;
        this.tagById = {};
        this.maxTagId = 0;
        this.acceptedTagIds = new Map();
        this.downstreamTagIds = new Map();
        this.internalTagsMap = {
            months: [],
            years: [],
            yearToDate: [],
            quarters: [],
            halfYears: [],
            versions: [],
        };
        this.tagsByLowercase = {};
        this.tagsByDisplay = {};
        this.tagsByParentId = {};
        this.relations = relations;
        this.relationsByTableTagId = {};
        this.tableTags = [];
        this.groupTags = [];
        this.accountTags = [];
        this.versionTagMap = {};
        this.relationConnectionsByKey = new Map();
        this.relationByKey = new Map();
        try {
            // create tag map by id
            for (var tags_1 = __values(tags), tags_1_1 = tags_1.next(); !tags_1_1.done; tags_1_1 = tags_1.next()) {
                var tag = tags_1_1.value;
                // create by id map
                this.tagById[tag.id] = tag;
                // creaete internal tags map
                if (isVersionTag(tag)) {
                    this.internalTagsMap.versions.push(tag);
                    this.versionTagMap[tag.name.toLowerCase()] = tag;
                }
                if (isMonthTag(tag)) {
                    this.internalTagsMap.months.push(tag);
                }
                if (isQuarterTag(tag)) {
                    this.internalTagsMap.quarters.push(tag);
                }
                if (isHalfYearTag(tag)) {
                    this.internalTagsMap.halfYears.push(tag);
                }
                if (isYearTag(tag)) {
                    this.internalTagsMap.years.push(tag);
                }
                if (isYearToDateTag(tag)) {
                    this.internalTagsMap.yearToDate.push(tag);
                }
                // create tag by lowercase name map
                var tagNameLowercase = getTagLowercaseName(tag);
                var tagsArray = this.tagsByLowercase[tagNameLowercase];
                if (tagsArray) {
                    tagsArray.push(tag);
                }
                else {
                    this.tagsByLowercase[tagNameLowercase] = [tag];
                }
                // create tag by name map
                var tagNameDisplay = getTagDisplayName(tag);
                var tagsNameArray = this.tagsByDisplay[tagNameDisplay];
                if (tagsNameArray) {
                    tagsNameArray.push(tag);
                }
                else {
                    this.tagsByDisplay[tagNameDisplay] = [tag];
                }
                // tags by parent id
                var sameParentArray = this.tagsByParentId[String(tag.ParentId)];
                if (sameParentArray) {
                    sameParentArray.push(tag);
                }
                else {
                    this.tagsByParentId[String(tag.ParentId)] = [tag];
                }
                if (tag.type === TagType.Table) {
                    this.tableTags.push(tag);
                }
                else if (tag.type === TagType.Account) {
                    this.accountTags.push(tag);
                }
                else if (tag.type === TagType.Group) {
                    this.groupTags.push(tag);
                }
                if (tag.type === TagType.Dimension) {
                    this.downstreamTagIds.set(tag.id, new Set([tag.id]));
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (tags_1_1 && !tags_1_1.done && (_a = tags_1.return)) _a.call(tags_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        var _loop_1 = function (tag) {
            var children = this_1.tagsByParentId[tag.id];
            if (children) {
                var set_1 = this_1.downstreamTagIds.get(tag.id);
                if (set_1) {
                    children.forEach(function (t) { return set_1 === null || set_1 === void 0 ? void 0 : set_1.add(t.id); });
                }
                else {
                    this_1.downstreamTagIds.set(tag.id, new Set(children.map(function (t) { return t.id; })));
                }
            }
        };
        var this_1 = this;
        try {
            for (var _g = __values(this.tableTags), _h = _g.next(); !_h.done; _h = _g.next()) {
                var tag = _h.value;
                _loop_1(tag);
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (_h && !_h.done && (_b = _g.return)) _b.call(_g);
            }
            finally { if (e_2) throw e_2.error; }
        }
        try {
            for (var relations_1 = __values(relations), relations_1_1 = relations_1.next(); !relations_1_1.done; relations_1_1 = relations_1.next()) {
                var relation = relations_1_1.value;
                var group = this.relationsByTableTagId[String(relation.tableTagId)];
                if (group) {
                    group.push(relation);
                }
                else {
                    this.relationsByTableTagId[String(relation.tableTagId)] = [relation];
                }
                this.relationByKey.set("".concat(relation.tableTagId, "-").concat(relation.connectedTableTagId), relation);
                try {
                    for (var _j = (e_4 = void 0, __values(relation.connections)), _k = _j.next(); !_k.done; _k = _j.next()) {
                        var connection = _k.value;
                        var set = this.downstreamTagIds.get(connection.connectedDimensionTagId);
                        if (set) {
                            set.add(connection.dimensionTagId);
                        }
                        else {
                            this.downstreamTagIds.set(connection.connectedDimensionTagId, new Set([connection.dimensionTagId]));
                        }
                        var key = "".concat(relation.tableTagId, "-").concat(relation.connectedTableTagId, "-").concat(connection.dimensionTagId);
                        var array = this.relationConnectionsByKey.get(key);
                        if (array) {
                            array.push(connection);
                        }
                        else {
                            this.relationConnectionsByKey.set(key, [connection]);
                        }
                    }
                }
                catch (e_4_1) { e_4 = { error: e_4_1 }; }
                finally {
                    try {
                        if (_k && !_k.done && (_d = _j.return)) _d.call(_j);
                    }
                    finally { if (e_4) throw e_4.error; }
                }
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (relations_1_1 && !relations_1_1.done && (_c = relations_1.return)) _c.call(relations_1);
            }
            finally { if (e_3) throw e_3.error; }
        }
        var visited = new Set();
        try {
            for (var _l = __values(this.downstreamTagIds), _m = _l.next(); !_m.done; _m = _l.next()) {
                var _o = __read(_m.value, 1), tagId = _o[0];
                this.calculateIndirectDownstreamTagIds(tagId, visited);
            }
        }
        catch (e_5_1) { e_5 = { error: e_5_1 }; }
        finally {
            try {
                if (_m && !_m.done && (_e = _l.return)) _e.call(_l);
            }
            finally { if (e_5) throw e_5.error; }
        }
        visited.clear();
        try {
            for (var _p = __values(this.tableTags), _q = _p.next(); !_q.done; _q = _p.next()) {
                var tag = _q.value;
                this.calculateAcceptedTagIds(tag.id, visited);
            }
        }
        catch (e_6_1) { e_6 = { error: e_6_1 }; }
        finally {
            try {
                if (_q && !_q.done && (_f = _p.return)) _f.call(_p);
            }
            finally { if (e_6) throw e_6.error; }
        }
    }
    TagData.prototype.calculateAcceptedTagIds = function (tableTagId, visited) {
        var e_7, _a, e_8, _b;
        if (visited.has(tableTagId)) {
            return;
        }
        visited.add(tableTagId);
        var accepted = this.acceptedTagIds.get(tableTagId);
        if (!accepted) {
            accepted = new Set();
            this.acceptedTagIds.set(tableTagId, accepted);
        }
        var dimensionTags = this.tagsByParentId[tableTagId];
        if (dimensionTags) {
            try {
                for (var dimensionTags_1 = __values(dimensionTags), dimensionTags_1_1 = dimensionTags_1.next(); !dimensionTags_1_1.done; dimensionTags_1_1 = dimensionTags_1.next()) {
                    var tag = dimensionTags_1_1.value;
                    accepted.add(tag.id);
                }
            }
            catch (e_7_1) { e_7 = { error: e_7_1 }; }
            finally {
                try {
                    if (dimensionTags_1_1 && !dimensionTags_1_1.done && (_a = dimensionTags_1.return)) _a.call(dimensionTags_1);
                }
                finally { if (e_7) throw e_7.error; }
            }
        }
        var relations = this.relationsByTableTagId[tableTagId];
        if (!relations) {
            return;
        }
        try {
            for (var relations_2 = __values(relations), relations_2_1 = relations_2.next(); !relations_2_1.done; relations_2_1 = relations_2.next()) {
                var relation = relations_2_1.value;
                this.calculateAcceptedTagIds(relation.connectedTableTagId, visited);
                var relationAccepted = this.acceptedTagIds.get(relation.connectedTableTagId);
                if (relationAccepted) {
                    relationAccepted.forEach(function (id) { return accepted === null || accepted === void 0 ? void 0 : accepted.add(id); });
                }
                var relationChildren = this.tagsByParentId[relation.connectedTableTagId];
                if (relationChildren) {
                    relationChildren.forEach(function (t) { return accepted === null || accepted === void 0 ? void 0 : accepted.add(t.id); });
                }
            }
        }
        catch (e_8_1) { e_8 = { error: e_8_1 }; }
        finally {
            try {
                if (relations_2_1 && !relations_2_1.done && (_b = relations_2.return)) _b.call(relations_2);
            }
            finally { if (e_8) throw e_8.error; }
        }
    };
    TagData.prototype.calculateIndirectDownstreamTagIds = function (tagId, visited) {
        var e_9, _a;
        if (visited.has(tagId)) {
            return;
        }
        visited.add(tagId);
        var ds = this.downstreamTagIds.get(tagId);
        if (!ds) {
            return;
        }
        try {
            for (var ds_1 = __values(ds), ds_1_1 = ds_1.next(); !ds_1_1.done; ds_1_1 = ds_1.next()) {
                var dsId = ds_1_1.value;
                this.calculateIndirectDownstreamTagIds(dsId, visited);
                var dsOfDsId = this.downstreamTagIds.get(dsId);
                if (dsOfDsId) {
                    dsOfDsId.forEach(function (id) { return ds.add(id); });
                }
            }
        }
        catch (e_9_1) { e_9 = { error: e_9_1 }; }
        finally {
            try {
                if (ds_1_1 && !ds_1_1.done && (_a = ds_1.return)) _a.call(ds_1);
            }
            finally { if (e_9) throw e_9.error; }
        }
    };
    TagData.prototype.search = function (value) {
        if (!this.fuse) {
            var config = {
                keys: [
                    { name: 'name', weight: 0.7 },
                    { name: 'description', weight: 0.3 },
                ],
                threshold: 0.1,
                minMatchCharLength: 2,
                useExtendedSearch: true,
                shouldSort: true,
                ignoreLocation: true,
            };
            this.fuse = new Fuse(this.tags, config);
        }
        if (value.length <= 1) {
            return [];
        }
        var searchStr = value;
        if (value.startsWith('*')) {
            searchStr = "".concat(value.replace('*', ''), "$");
        }
        else if (value.endsWith('*')) {
            searchStr = "^".concat(value.replace('*', ''));
        }
        else if (value.includes(',')) {
            searchStr = "".concat(value
                .split(',')
                .map(function (v) { return v.trim(); })
                .join('|'));
        }
        //const pattern = searchStr.replace(' ', '');
        var res = this.fuse.search(searchStr);
        return res.map(function (result) { return result.item; });
    };
    TagData.prototype.searchTagsAndReturnIds = function (value, filterByTypes, filterByInternalTypes, filterByParentIds) {
        var e_10, _a, e_11, _b;
        var filters = [
            {
                shouldAdd: function (tag) {
                    if (!filterByTypes) {
                        return true;
                    }
                    return filterByTypes.includes(tag.type);
                },
            },
            {
                shouldAdd: function (tag) {
                    if (!filterByInternalTypes) {
                        return true;
                    }
                    return !!tag.internalType && filterByInternalTypes.includes(tag.internalType);
                },
            },
            {
                shouldAdd: function (tag) {
                    var _a;
                    if (!filterByParentIds) {
                        return true;
                    }
                    return filterByParentIds.includes((_a = tag.ParentId) !== null && _a !== void 0 ? _a : 0);
                },
            },
        ];
        var tags = value.length > 0 ? this.search(value) : this.tags;
        var foundTagIds = new Set();
        try {
            for (var tags_2 = __values(tags), tags_2_1 = tags_2.next(); !tags_2_1.done; tags_2_1 = tags_2.next()) {
                var tag = tags_2_1.value;
                var shouldAddTag = true;
                try {
                    for (var filters_1 = (e_11 = void 0, __values(filters)), filters_1_1 = filters_1.next(); !filters_1_1.done; filters_1_1 = filters_1.next()) {
                        var filter = filters_1_1.value;
                        shouldAddTag = shouldAddTag && filter.shouldAdd(tag);
                    }
                }
                catch (e_11_1) { e_11 = { error: e_11_1 }; }
                finally {
                    try {
                        if (filters_1_1 && !filters_1_1.done && (_b = filters_1.return)) _b.call(filters_1);
                    }
                    finally { if (e_11) throw e_11.error; }
                }
                if (shouldAddTag) {
                    foundTagIds.add(tag.id);
                }
            }
        }
        catch (e_10_1) { e_10 = { error: e_10_1 }; }
        finally {
            try {
                if (tags_2_1 && !tags_2_1.done && (_a = tags_2.return)) _a.call(tags_2);
            }
            finally { if (e_10) throw e_10.error; }
        }
        return foundTagIds;
    };
    TagData.prototype.getTagByNameAndParent = function (tagName, parentId) {
        var tags = this.tagsByLowercase[tagName.toLowerCase()];
        if (!tags) {
            return null;
        }
        var filtered = tags.filter(function (t) { return t.ParentId === parentId; });
        return filtered[0];
    };
    TagData.prototype.getTagByDisplayNameAndParent = function (tagName, parentId) {
        var tags = this.tagsByDisplay[tagName];
        if (!tags) {
            return null;
        }
        var filtered = tags.filter(function (t) { return t.ParentId === parentId; });
        return filtered[0];
    };
    TagData.prototype.getTagByPath = function (tagPath) {
        var e_12, _a;
        var currentTag = null;
        var cannonicalPath = tagPath.map(function (segment) { return getCannonicalTagName(segment); });
        var _loop_2 = function (pathSegment) {
            var tagsMatchingSegment = this_2.tagsByLowercase[pathSegment];
            if (!tagsMatchingSegment) {
                return { value: null };
            }
            if (tagsMatchingSegment.length === 1 && currentTag === null) {
                currentTag = tagsMatchingSegment[0];
                return "continue";
            }
            var parentId = currentTag ? currentTag.id : null;
            var tagsMatchingParent = tagsMatchingSegment.filter(function (t) { return t.ParentId === parentId; });
            if (tagsMatchingParent.length === 1) {
                currentTag = tagsMatchingParent[0];
            }
            else {
                return { value: null };
            }
        };
        var this_2 = this;
        try {
            for (var cannonicalPath_1 = __values(cannonicalPath), cannonicalPath_1_1 = cannonicalPath_1.next(); !cannonicalPath_1_1.done; cannonicalPath_1_1 = cannonicalPath_1.next()) {
                var pathSegment = cannonicalPath_1_1.value;
                var state_1 = _loop_2(pathSegment);
                if (typeof state_1 === "object")
                    return state_1.value;
            }
        }
        catch (e_12_1) { e_12 = { error: e_12_1 }; }
        finally {
            try {
                if (cannonicalPath_1_1 && !cannonicalPath_1_1.done && (_a = cannonicalPath_1.return)) _a.call(cannonicalPath_1);
            }
            finally { if (e_12) throw e_12.error; }
        }
        return currentTag;
    };
    TagData.prototype.createTagList = function (tags, depth, tagListOptions) {
        var e_13, _a;
        var _b;
        var rows = [];
        var searchEnabled = tagListOptions.searchTagIds !== undefined;
        try {
            for (var tags_3 = __values(tags), tags_3_1 = tags_3.next(); !tags_3_1.done; tags_3_1 = tags_3.next()) {
                var tag = tags_3_1.value;
                var expanded = tagListOptions.expandedTagIds ? tagListOptions.expandedTagIds.has(tag.id) : true;
                var children = this.tagsByParentId[tag.id];
                var childRows = [];
                if ((searchEnabled || expanded) && children && children.length > 0) {
                    childRows = this.createTagList(children, depth + 1, tagListOptions);
                }
                /*
                    add current tag to rows
                    * if search is not enabled
                    OR
                    * if search is enabled AND there are children
                    OR
                    * search is enabled AND there are no children BUT this tag is in the search results
                */
                if ((!searchEnabled || childRows.length > 0 || ((_b = tagListOptions.searchTagIds) === null || _b === void 0 ? void 0 : _b.has(tag.id))) &&
                    !(tagListOptions.excludeInternalTags && isInternalTag(tag))) {
                    rows.push({
                        id: tag.id,
                        name: tag.name,
                        expanded: expanded || (searchEnabled && childRows.length > 0),
                        depth: depth,
                        hasChildren: (searchEnabled && childRows.length > 0) || (children && children.length > 0) || false,
                        tag: tag,
                        connectedTagColumns: [],
                        disabled: tagListOptions.disabledTagIds ? tagListOptions.disabledTagIds.has(tag.id) : false,
                    });
                }
                rows = rows.concat(childRows);
            }
        }
        catch (e_13_1) { e_13 = { error: e_13_1 }; }
        finally {
            try {
                if (tags_3_1 && !tags_3_1.done && (_a = tags_3.return)) _a.call(tags_3);
            }
            finally { if (e_13) throw e_13.error; }
        }
        return rows;
    };
    return TagData;
}());
export { TagData };
